/**
 * Ivy port of the `ng2-flatpickr` component (ISC licence, https://github.com/Mikael-R-Lindberg/ng2-flatpickr).
 * The npm package is a View Engine library that no longer compiles on Angular 16+ (ngcc is gone) and
 * is unmaintained; only the component is used by WebBooker, so only that is ported. Selector, inputs and
 * the public `flatpickr` / `flatpickrElement` members are unchanged.
 */
import {
  AfterViewInit, Component, ElementRef, forwardRef, Input, OnChanges, type SimpleChanges, ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import 'flatpickr';

@Component({
    selector: 'ng2-flatpickr',
    template: `
    <div class="ng2-flatpickr-input-container" #flatpickr>
      @if (!hideButton) {
        <input class="ng2-flatpickr-input {{ addClass }}" [placeholder]="placeholder" [tabindex]="tabindex" type="text" (focus)="onFocus($event)" data-input>
      }
      <ng-content></ng-content>
    </div>
    `,
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => Ng2FlatpickrComponent), multi: true }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class Ng2FlatpickrComponent implements AfterViewInit, OnChanges, ControlValueAccessor {
  @ViewChild('flatpickr', {static: true}) flatpickrElement: ElementRef;
  @Input() config: any;
  @Input() placeholder = '';
  @Input() addClass = '';
  @Input() setDate: any;
  @Input() hideButton = false;

  @Input()
  get tabindex(): number { return this._tabindex; }
  set tabindex(ti: number) { this._tabindex = Number(ti); }

  flatpickr: any;

  private _tabindex = 0;
  private onTouchedFn: () => void = () => {};
  private propagateChange: (_: any) => void = () => {};
  private defaultFlatpickrOptions: any = {
    wrap: true,
    clickOpens: true,
    onChange: (selectedDates: Date[]) => { this.writeValue(selectedDates); }
  };

  writeValue(value: any): void {
    this.propagateChange(value);
  }

  registerOnChange(fn: (_: any) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDateFromInput(date: any): void {
    this.flatpickrElement.nativeElement._flatpickr.setDate(date, true);
  }

  setAltInputPlaceholder(placeholder: string): void {
    this.flatpickrElement.nativeElement._flatpickr.altInput.setAttribute('placeholder', placeholder);
  }

  ngAfterViewInit(): void {
    if (this.config) {
      Object.assign(this.defaultFlatpickrOptions, this.config);
    }
    if (this.flatpickrElement.nativeElement.flatpickr) {
      this.flatpickr = this.flatpickrElement.nativeElement.flatpickr(this.defaultFlatpickrOptions);
    }
    if (this.setDate) {
      this.setDateFromInput(this.setDate);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.flatpickrElement.nativeElement && this.flatpickrElement.nativeElement._flatpickr) {
      if (changes.hasOwnProperty('setDate') && changes['setDate'].currentValue) {
        this.setDateFromInput(changes['setDate'].currentValue);
      }
      if (this.config.altInput && changes.hasOwnProperty('placeholder') && changes['placeholder'].currentValue) {
        this.setAltInputPlaceholder(changes['placeholder'].currentValue);
      }
    }
  }

  onFocus(_event: Event): void {
    this.onTouchedFn();
  }
}
